import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { FormControl, FormGroup, ValidationErrors, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import intlTelInput, { Iti } from 'intl-tel-input';
import { es, pt } from "intl-tel-input/i18n";
import { FilterOperator, FilterOptionsType, ISAPI, ISFilterAPI, ISPagination } from "is-lib";
import { catchError } from "rxjs";
import { RegExpPatterns } from "src/app/helpers/reg-exp-patterns";
import { ContractAccessRequest, ContractAccessRequestResponse } from "src/app/models/ContractAccessRequest";
import { HistoryComparison } from "src/app/models/HistoryComparison";
import { ServerResponse } from "src/app/models/ServerResponse";
import { SnackBarType } from "src/app/models/Snackbar";
import { TicketById } from "src/app/models/TicketById";
import { ContractsAccessRequestsService } from "src/app/services/contract-access-requests.service";
import { ContractsService } from "src/app/services/contracts.service";
import { SnackBarService } from "src/app/services/snackbar.service";
import { UsersService } from "src/app/services/users.service";

@Component({
    selector: 'contract-access',
    templateUrl: './contract-access.component.html',
    styleUrls: ['./contract-access.component.scss']
})

export class ContractAccessComponent implements OnInit, OnDestroy {

    isAPI!: ISAPI;

    accessRequestForm = new FormGroup({
        tin: new FormControl<string>('', Validators.compose([Validators.required, Validators.minLength(9), Validators.maxLength(9)])),
        email: new FormControl<string>('', [Validators.required, Validators.pattern(RegExpPatterns.email)]),
        contactNumber: new FormControl<Iti | null>(null),
    })


    @ViewChild('phone') set phoneInputSetup(phoneInput: ElementRef) {
        if (phoneInput != null) {
            if (this.accessRequestForm.controls.contactNumber.value == null) {
                const iti = intlTelInput(
                    phoneInput.nativeElement,
                    {
                        i18n: this._usersService.userCountryCode! == 'PT' ? pt : es,
                        initialCountry: this._usersService.userCountryCode!,
                        separateDialCode: true,
                        strictMode: true,
                        placeholderNumberType: 'FIXED_LINE_OR_MOBILE',
                        validationNumberTypes: ['FIXED_LINE_OR_MOBILE'],
                        formatAsYouType: true,
                        formatOnDisplay: true
                    }
                )
                this.accessRequestForm.controls.contactNumber.setValue(iti);
                iti.setNumber(this._usersService.userData?.contactNumber ?? '')
            }
            
        } else {
            this.accessRequestForm.controls.contactNumber?.value?.destroy();
            this.accessRequestForm.controls.contactNumber?.setValue(null);
        }

    }


    constructor(private _contractsService: ContractsService,
                private _snackbarService: SnackBarService,
                private _contractAccessRequestsService: ContractsAccessRequestsService,
                private _usersService: UsersService,
                private readonly dialogRef: MatDialogRef<ContractAccessComponent>
    ) { 
        this.isAPI = new ISAPI([
            new ISFilterAPI('contactNumber', '', FilterOptionsType.text, FilterOperator.equals, undefined, undefined),
            new ISFilterAPI('contactEmail', '', FilterOptionsType.text, FilterOperator.equals, undefined, undefined),
            new ISFilterAPI('entityTIN', '', FilterOptionsType.text,FilterOperator.equals, undefined, undefined)
        ])
    }
 
    // The phone input's ViewChild setter isn't called when the dialog closes, and intl-tel-input keeps its instances and listeners until destroy()
    ngOnDestroy(): void {
        this.accessRequestForm.controls.contactNumber.value?.destroy();
    }

    ngOnInit(): void {
        if(this._usersService.userData){
            if(this._usersService.userData.email)
                this.accessRequestForm.controls.email.setValue(this._usersService.userData.email);

            if(this._usersService.userData.tin)
                this.accessRequestForm.controls.tin.setValue(this._usersService.userData.tin);

            if(this._usersService.userData.contactNumber) {
                const contactNumberControl = this.accessRequestForm.controls.contactNumber.value;
                contactNumberControl?.setNumber(this._usersService.userData.contactNumber);
            }
        }
    }

    getAccessRequest() {      
        let body: ContractAccessRequest = {
            contactNumber: String(this.accessRequestForm.value.contactNumber),
            contactEmail: this.accessRequestForm.value.email || '',
            entityTIN: this.accessRequestForm.value.tin || '',
        };

        if(!this.accessRequestForm.valid){
            let errors = [];
            errors.push({ name: 'FormGroup', errors: this.accessRequestForm.errors });
            errors = Object.entries(this.accessRequestForm.controls)
                .filter((control) => control[1].errors != null)
                .map((control) => {
                    return { name: control[0], errors: control[1].errors! };
                });

            this.showSnackbarAccordingToValidationErrors(errors);
        }
        else{
            this._contractsService.getContractAccessRequest(body, true).subscribe({
                //It does not give error to the frontend, the object is simply null!!
                next: (res: ServerResponse<TicketById> | ServerResponse<number>) => {
                    try {
                        if (res && res.data) {
                            if(typeof res.data === 'number'){
                                this._snackbarService.callSnackBar('VERIFY_EMAIL_ACCESS_TO_CONTRACTS', SnackBarType.SUCCESS);

                            }
                            else {
                                this._snackbarService.callSnackBar('A_TICKET_WAS_CREATED', SnackBarType.SUCCESS, undefined, undefined, undefined, { ticketNumber: res.data.number });
                            }
                            this.dialogRef.close(true);
                            this.clearInputs();
                        }
                        else if(!res.data && res.statusCode === 500)
                            this._snackbarService.callSnackBar('REQUEST_ALREADY_EXISTS', SnackBarType.WARNING);
                    } catch (error) {
                    }
                },
                error: (error) => {
                    if (error.status === 401) {
                        if(!error.ok)
                            this._snackbarService.callSnackBar('ERROR_UNAUTHORIZED', SnackBarType.ERROR);
                        else
                            this._snackbarService.callSnackBar('DATA_NOT_FOUND', SnackBarType.WARNING);

                    } else if (error.status === 405 && error.message.toUpperCase().includes('Unauthorized'.toUpperCase())) {
                        this._snackbarService.callSnackBar('ERROR_FORBIDDEN_CONTRACT_ACCESS', SnackBarType.WARNING);
                    }
                    else if (error.status === 409){
                        this._snackbarService.callSnackBar('REQUEST_ALREADY_EXISTS', SnackBarType.WARNING);
                    }

                    else if (error.status === 400 ){
                        this._snackbarService.callSnackBar('REQUEST_SUCCESSFUL', SnackBarType.ERROR);
                    }
                    else if (error.status === 405 && error.message.toUpperCase().includes('Not Allowed'.toUpperCase())){
                        this._snackbarService.callSnackBar('NOT_PERMITTED', SnackBarType.ERROR);
                    }
                    else if(error.status === 500){
                        this._snackbarService.callSnackBar('INTERNAL_SERVER_ERROR', SnackBarType.ERROR);
                    }
                    else if(error.status === 405){
                        this._snackbarService.callSnackBar('REQUEST_ALREADY_EXISTS', SnackBarType.WARNING);
                    }
                }
            });
        }
        
    }

    clearInputs() {
        this.accessRequestForm.controls.contactNumber.value?.setNumber('');
        this.accessRequestForm.controls.email.setValue('');
        this.accessRequestForm.controls.tin.setValue('');
    }

    showSnackbarAccordingToValidationErrors(errorObjects: { name: string, errors: ValidationErrors }[]) {
        if (errorObjects && errorObjects.length > 0) {
            if (errorObjects.some( (error: { name: string, errors: ValidationErrors }) => error.errors['required'])) {
                this._snackbarService.callSnackBar('FILL_ALL_REQUIRED_FIELDS', SnackBarType.WARNING);
                return;
            }

            let emailErrors: ValidationErrors[] = errorObjects.filter( (error: { name: string, errors: ValidationErrors }) => error.name == 'email').map((error) => error.errors)
            if (emailErrors.length > 0) {
                this._snackbarService.callSnackBar('INVALID_EMAIL', SnackBarType.WARNING);
                return;
            }

            let phoneErrors: ValidationErrors[] = errorObjects.filter( (error: { name: string, errors: ValidationErrors }) => error.name == 'phone').map((error) => error.errors)
            if(phoneErrors.length > 0){
                this._snackbarService.callSnackBar('INVALID_PHONE', SnackBarType.WARNING);
                return;
            }

            let tinErrors: ValidationErrors[] = errorObjects.filter( (error: { name: string, errors: ValidationErrors }) => error.name == 'phone').map((error) => error.errors)
            if(tinErrors.length > 0){
                this._snackbarService.callSnackBar('INVALID_TIN', SnackBarType.WARNING);
                return;
            }
        }
    }
}